// Jordan time: every date Ibraheem sees is shown in Asia/Amman, whatever device he is on.
export const TIME_ZONE = 'Asia/Amman';

const DEFAULT: Intl.DateTimeFormatOptions = {
  weekday: 'short',
  day: 'numeric',
  month: 'short',
  hour: '2-digit',
  minute: '2-digit',
};

/** "Tue 20 Oct, 09:00" in Jordan time */
export function formatJordan(iso: string | Date, opts: Intl.DateTimeFormatOptions = {}): string {
  return new Date(iso).toLocaleString('en-GB', { timeZone: TIME_ZONE, ...DEFAULT, ...opts });
}

/** "2026-10-20" for a moment, in Jordan time */
export function jordanDate(d: Date = new Date()): string {
  return d.toLocaleDateString('en-CA', { timeZone: TIME_ZONE });
}

/** "2026-10-20T11:00:00+03:00": the same moment as a UTC timestamp, written in Jordan time (what Claude reads and writes) */
export function jordanIso(iso: string | Date): string {
  const d = new Date(iso);
  const p = Object.fromEntries(
    new Intl.DateTimeFormat('en-CA', {
      timeZone: TIME_ZONE,
      hourCycle: 'h23',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      timeZoneName: 'longOffset',
    })
      .formatToParts(d)
      .map((x) => [x.type, x.value]),
  );
  const offset = (p.timeZoneName ?? 'GMT').replace('GMT', '') || '+00:00';
  return `${p.year}-${p.month}-${p.day}T${p.hour}:${p.minute}:${p.second}${offset}`;
}

const UTC_STAMP = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d+)?)?Z$/;

/** Every UTC timestamp ("…Z") inside a value, rewritten in Jordan time, so nobody has to add 3 hours in their head */
export function inJordanTime<T>(value: T): T {
  if (typeof value === 'string') return (UTC_STAMP.test(value) ? jordanIso(value) : value) as T;
  if (Array.isArray(value)) return value.map(inJordanTime) as T;
  if (value && typeof value === 'object')
    return Object.fromEntries(Object.entries(value).map(([k, v]) => [k, inJordanTime(v)])) as T;
  return value;
}

/** 2025-07-23 → "23 Jul 2025" (a plain calendar date, no time zone involved) */
export const shortDate = (iso: string): string =>
  new Date(iso + 'T00:00:00Z').toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  });
